import { drugAction } from '@/src/api'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@/src/zustand/Modal'
import { useDrugs, useEncounter, useMedication } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { CaretDownIcon, CaretRightIcon } from '@icons'
import { frequencySchema, medicationSchema } from '@schema/pDirectory.schema'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { hideAddMedications, hideMedicationFrequency, showMedicationFrequency } from '@store/slicers/ModalSlicer'
import { palette } from '@themes'
import { FrequencyData } from '@type/medication.type'
import {
  DateInputField,
  FormButton,
  FormInputField,
  Input,
  Modal,
  RadioBtn,
  SearchInput,
  SelectInputField,
  TextAreaField,
  TimeInputField,
  ToggleBtn,
  Button
} from '@ui'
import { Select } from '@ui/SearchInput/Select'
import {
  capitalizeDrugName,
  getError,
  isAllergyError,
  normalizeHourlyFrequency,
  removeEmptyValues,
  showToast
} from '@util'
import { useCustomOptionStore } from '@zustore/store'
import { Box, CheckBox, DropButton, Text } from 'grommet'
import moment from 'moment'
import React, { useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FaPlus } from 'react-icons/fa'
import { useParams, useSearchParams } from 'react-router-dom'

const STRENGTH_REGEX = /^\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+(?:\s*\+\s*\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+)*$/
const PERIODS_OF_DAY = ['morning', 'afternoon', 'night'] as const
const DEFAULT_END_AT = () => new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString()

type SessionMedSnapshot = {
  nf_drug_name?: string
  form?: string
  strength?: string
  dose_quantity?: string | number
  dose_unit: string
  route?: string
  medication_type?: string
  start_at?: string
  end_at?: string
  comment?: string
  administer_at_hospital?: boolean
  selectedFrequency: string
  freqData: FrequencyData | null
  isCustomFrequencySelected: boolean
}

type SessionMedStatus = 'pending' | 'saved' | 'error'

type SessionMed = {
  clientId: string
  id: string | null
  label: string
  snapshot: SessionMedSnapshot
  status: SessionMedStatus
  payload: any
}

const buildSessionLabel = (data: { nf_drug_name?: string; strength?: string; medication_type?: string }) => {
  const strengthLabel = data.strength ? ` ${data.strength}` : ''
  const urgencyLabel = data.medication_type ? ` — ${data.medication_type}` : ''
  return `${capitalizeDrugName(data.nf_drug_name || 'Medication')}${strengthLabel}${urgencyLabel}`
}

const formatPeriods = (periods: string[]) => {
  if (periods.length > 2) {
    return `${periods.slice(0, -1).join(', ')}, and ${periods[periods.length - 1]}`
  }
  return periods.join(' and ')
}

export const renderFrequencyOptions = (frequencyData?: FrequencyData): string => {
  if (!frequencyData) return ''

  const { interval = '', periods = [], type = '' } = frequencyData

  if (type === 'hourly') return interval
  if (type === 'daily') {
    return periods.length > 0 ? `Every ${formatPeriods(periods)}` : 'Daily'
  }
  return ''
}

const getMedicationContext = (encounterType?: string, isDischargeTab?: boolean) => {
  if (encounterType === 'outpatient') {
    return isDischargeTab ? 'outpatient' : 'outpatient_administered'
  }
  return isDischargeTab ? 'post_discharge' : 'admitted'
}

interface DropDownMenuProps {
  onCustomSelect: () => void
  onOptionSelect: (value: string) => void
  selectedValue: string
  label: string
  placeholder?: string
}

export const DropDownMenu: React.FC<DropDownMenuProps> = ({
  onCustomSelect,
  onOptionSelect,
  selectedValue,
  label,
  placeholder
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const { medFrequencies = [] } = useMedication()
  const { setIsCustomFrequencySelected } = useCustomOptionStore()

  const frequencyOptions = useMemo(
    () => [
      ...medFrequencies.map(med_freq => ({
        name: med_freq?.attributes?.code,
        value: med_freq?.attributes?.name
      })),
      { name: 'Custom', value: '' }
    ],
    [medFrequencies]
  )

  const handleSelect = (option: { name: string; value: string }) => {
    if (option.name === 'Custom') {
      setIsCustomFrequencySelected(true)
      onCustomSelect()
    } else {
      setIsCustomFrequencySelected(false)
      onOptionSelect(option.value)
    }
    setShowMenu(false)
  }

  return (
    <>
      {label && (
        <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
          {label}
        </Text>
      )}
      <DropButton
        dropContent={
          <Box pad="4px">
            {frequencyOptions.map(option => (
              <Box
                align="center"
                direction="row"
                hoverIndicator="#F5F5F5"
                justify="between"
                key={option.name}
                onClick={() => handleSelect(option)}
                pad="8px"
              >
                <Box align="center" className="!w-full pr-2" direction="row" gap="small" justify="between">
                  <Text color="#333333" size="small">
                    {option.name}
                  </Text>
                  <Text color="#808080" size="small">
                    {option.value}
                    {option.name === 'Custom' && <CaretRightIcon />}
                  </Text>
                </Box>
                {selectedValue === option.value && (
                  <svg fill="none" height="11" viewBox="0 0 12 11" width="12" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0.75 6.875C0.75 6.875 1.875 6.875 3.375 9.5C3.375 9.5 7.54412 2.625 11.25 1.25"
                      stroke="#141B34"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                )}
              </Box>
            ))}
          </Box>
        }
        dropProps={{
          align: { top: 'bottom' },
          margin: { left: '12px', top: '6px' },
          round: '8px',
          stretch: true,
          style: { minWidth: '200px' }
        }}
        onClose={() => setShowMenu(false)}
        onOpen={() => setShowMenu(true)}
        open={showMenu}
      >
        <Box
          border={{ color: '#999999' }}
          className="relative"
          direction="row"
          justify="between"
          pad="small"
          round="999px"
        >
          <Text size="small" weight={selectedValue ? 'bold' : undefined}>
            {selectedValue}
            <span className="font-[450] text-[#999999]">{!selectedValue && (placeholder || 'Select Frequency')}</span>
          </Text>
          <CaretDownIcon />
        </Box>
      </DropButton>
    </>
  )
}

interface AddMedicationProps {
  discharge?: boolean
}

const AddMedication: React.FC<AddMedicationProps> = ({ discharge }) => {
  const { patientId, encounterId } = useParams<{ patientId: string; encounterId: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAppSelector(state => state.user)
  const { custom_frequency } = useAppSelector(state => state.modal.medication)
  const { dosage_forms, routes, dosage_unit } = useAppSelector(state => state.config)
  const dispatch = useAppDispatch()
  const { openModal, closeModal } = useModalStore()
  const { isCustomFrequencySelected, setIsCustomFrequencySelected } = useCustomOptionStore()
  const { createMedication, updateMedication, medFrequencies } = useMedication({ patientId })
  const { singleEncounter } = useEncounter({ id: encounterId })
  const allergyWarningOpenRef = useRef(false)
  const sessionAddedRef = useRef<SessionMed[]>([])

  const methods = useForm({
    defaultValues: {
      end_at: DEFAULT_END_AT(),
      medication_type: 'Routine',
      start_at: new Date().toISOString()
    },
    resolver: yupResolver(medicationSchema)
  })
  const freqMethods = useForm({
    defaultValues: { type: 'hourly' as const },
    resolver: yupResolver(frequencySchema)
  })

  const drugName = methods.watch('nf_drug_name')
  const formValue = methods.watch('form')
  const { drugSearch, createDrug } = useDrugs({ query: drugName, form: formValue })

  const [freqData, setFreqData] = useState<FrequencyData | null>(null)
  const [isHourly, setIsHourly] = useState(true)
  const [selectedFrequency, setSelectedFrequency] = useState('')
  const [doseUnit, setDoseUnit] = useState('mg')
  const [customOptions, setCustomOptions] = useState<{ label: string; value: string }[]>([])
  const [timeFields, setTimeFields] = useState([''])
  const [toggleStates, setToggleStates] = useState({ Period: false, Specific_time: false })
  const [sessionAdded, setSessionAdded] = useState<SessionMed[]>([])
  const [editingClientId, setEditingClientId] = useState<string | null>(null)
  sessionAddedRef.current = sessionAdded

  const isDischargeTab = searchParams.get('encounterTab') === 'Discharge' || !!discharge
  const isEditing = !!editingClientId

  const resetFormForNextOrder = () => {
    methods.reset({
      end_at: DEFAULT_END_AT(),
      medication_type: 'Routine',
      start_at: new Date().toISOString()
    })
    freqMethods.reset({ type: 'hourly' as const })
    setFreqData(null)
    setIsHourly(true)
    setSelectedFrequency('')
    setDoseUnit('mg')
    setCustomOptions([])
    setTimeFields([''])
    setToggleStates({ Period: false, Specific_time: false })
    setIsCustomFrequencySelected(false)
    setEditingClientId(null)
  }

  const buildSnapshot = (data: any): SessionMedSnapshot => ({
    administer_at_hospital: data.administer_at_hospital,
    comment: data.comment,
    dose_quantity: data.dose_quantity,
    dose_unit: doseUnit,
    end_at: data.end_at,
    form: data.form,
    freqData,
    isCustomFrequencySelected,
    medication_type: data.medication_type,
    nf_drug_name: data.nf_drug_name,
    route: data.route,
    selectedFrequency,
    start_at: data.start_at,
    strength: data.strength
  })

  const loadSnapshotIntoForm = (snapshot: SessionMedSnapshot) => {
    methods.reset({
      administer_at_hospital: snapshot.administer_at_hospital ?? false,
      comment: snapshot.comment || '',
      dose_quantity:
        snapshot.dose_quantity != null && snapshot.dose_quantity !== '' ? String(snapshot.dose_quantity) : '',
      end_at: snapshot.end_at || DEFAULT_END_AT(),
      form: snapshot.form || '',
      medication_type: snapshot.medication_type || 'Routine',
      nf_drug_name: snapshot.nf_drug_name || '',
      route: snapshot.route || '',
      start_at: snapshot.start_at || new Date().toISOString(),
      strength: snapshot.strength || ''
    })
    setDoseUnit(snapshot.dose_unit || 'mg')
    setSelectedFrequency(snapshot.selectedFrequency || '')
    setFreqData(snapshot.freqData)
    setIsCustomFrequencySelected(snapshot.isCustomFrequencySelected)
    setCustomOptions([])
    const freqType = snapshot.freqData?.type
    setIsHourly(freqType !== 'daily')
    if (freqType === 'daily') {
      setToggleStates({
        Period: !!snapshot.freqData?.periods?.length,
        Specific_time: !!snapshot.freqData?.specific_times?.length
      })
      freqMethods.reset({
        type: 'daily',
        interval: '',
        periods: snapshot.freqData?.periods || [],
        specific_times: snapshot.freqData?.specific_times || []
      })
      setTimeFields(
        snapshot.freqData?.specific_times?.length
          ? snapshot.freqData.specific_times.map((_, i) => `specific_times_${i}`)
          : ['']
      )
    } else {
      setToggleStates({ Period: false, Specific_time: false })
      freqMethods.reset({
        type: 'hourly',
        interval: snapshot.freqData?.interval || '',
        periods: [],
        specific_times: []
      })
      setTimeFields([''])
    }
  }

  const handleEditSessionMed = (item: SessionMed) => {
    if (item.status === 'pending') return
    setEditingClientId(item.clientId)
    loadSnapshotIntoForm(item.snapshot)
  }

  const cancelEdit = () => {
    resetFormForNextOrder()
  }

  const markSessionStatus = (
    clientId: string,
    patch: Partial<Pick<SessionMed, 'status' | 'id' | 'label' | 'snapshot' | 'payload'>>
  ) => {
    setSessionAdded(prev => prev.map(item => (item.clientId === clientId ? { ...item, ...patch } : item)))
  }

  const withAllergyOverride = (payload: any) => ({
    ...payload,
    data: {
      ...payload.data,
      attributes: {
        ...payload.data.attributes,
        override_allergy: true
      }
    }
  })

  const presentAllergyWarning = ({
    clientId,
    snapshot,
    onContinue
  }: {
    clientId: string
    snapshot: SessionMedSnapshot
    onContinue: () => void
  }) => {
    if (allergyWarningOpenRef.current) {
      return
    }
    allergyWarningOpenRef.current = true
    let continued = false

    openModal(DOCTOR_MODAL_TYPE.ALLERGY_WARNING, {
      onSubmit: () => {
        continued = true
        onContinue()
      },
      onClose: () => {
        allergyWarningOpenRef.current = false
        closeModal(DOCTOR_MODAL_TYPE.ALLERGY_WARNING)
        if (!continued) {
          setEditingClientId(clientId)
          loadSnapshotIntoForm(snapshot)
        }
      }
    })
  }

  const runCreateInBackground = (clientId: string, payload: any) => {
    void createMedication(payload)
      .then(res => {
        const medicationId = res?.data?.id
        if (!medicationId) {
          markSessionStatus(clientId, { status: 'error' })
          showToast('Medication added, but could not track it for editing')
          return
        }
        markSessionStatus(clientId, { id: medicationId, status: 'saved', payload })
        showToast('Medication added successfully')
      })
      .catch(error => {
        markSessionStatus(clientId, { status: 'error', payload })
        if (!isAllergyError(error)) {
          getError(error)
          return
        }

        const snapshot =
          sessionAddedRef.current.find(item => item.clientId === clientId)?.snapshot ||
          ({
            dose_unit: 'mg',
            selectedFrequency: '',
            freqData: null,
            isCustomFrequencySelected: false
          } as SessionMedSnapshot)

        presentAllergyWarning({
          clientId,
          snapshot,
          onContinue: () => {
            const overridden = withAllergyOverride(payload)
            markSessionStatus(clientId, { status: 'pending', payload: overridden })
            runCreateInBackground(clientId, overridden)
          }
        })
      })
  }

  const runUpdateInBackground = (
    clientId: string,
    medicationId: string,
    payload: any,
    label: string,
    snapshot: SessionMedSnapshot
  ) => {
    void updateMedication({ formVal: payload, id: medicationId })
      .then(() => {
        markSessionStatus(clientId, { status: 'saved', label, snapshot, payload })
        showToast('Medication updated successfully')
      })
      .catch(error => {
        markSessionStatus(clientId, { status: 'error', label, snapshot, payload })
        if (!isAllergyError(error)) {
          getError(error)
          return
        }

        presentAllergyWarning({
          clientId,
          snapshot,
          onContinue: () => {
            const overridden = withAllergyOverride(payload)
            markSessionStatus(clientId, { status: 'pending', label, snapshot, payload: overridden })
            runUpdateInBackground(clientId, medicationId, overridden, label, snapshot)
          }
        })
      })
  }

  const availableStrengthOptions = useMemo(() => {
    const baseOptions =
      drugSearch?.flatMap(
        drug =>
          drug.available_strengths?.map(combination => {
            const combinedLabel = combination?.map(s => s.strength).join('/')
            return { label: combinedLabel, value: combinedLabel }
          }) ?? []
      ) ?? []

    return [...baseOptions, ...customOptions]
  }, [drugSearch, customOptions])

  const drugVal = useMemo(
    () => (drugName ? { label: capitalizeDrugName(drugName), value: drugName } : { label: '', value: '' }),
    [drugName]
  )

  const handleToggle = (id: string, state: boolean) => {
    setToggleStates(prev => {
      if (id === 'Period') {
        return { Period: state, Specific_time: state ? false : prev.Specific_time }
      }
      if (id === 'Specific_time') {
        return { Specific_time: state, Period: state ? false : prev.Period }
      }
      return prev
    })
  }

  const handleHourlyCheck = () => {
    setToggleStates({ Period: false, Specific_time: false })
    setIsHourly(true)
    freqMethods.setValue('periods', [])
    freqMethods.setValue('specific_times', [])
  }

  const handleDailyCheck = () => {
    setToggleStates({ Period: true, Specific_time: false })
    setIsHourly(false)
    freqMethods.setValue('interval', '')
  }

  const onClose = () => {
    if (sessionAdded.some(item => item.status === 'pending')) {
      showToast('Saving continues in the background.')
    }
    dispatch(hideAddMedications())
  }
  const onFrequencyModalClose = () => dispatch(hideMedicationFrequency())

  const onFreqSubmit = (data: any) => {
    if (data.type === 'hourly' && data.interval) {
      const normalized = normalizeHourlyFrequency(data.interval)
      if (normalized) data.interval = normalized
    }
    setFreqData(data)
    setSelectedFrequency(renderFrequencyOptions(data))
    setIsCustomFrequencySelected(true)
    onFrequencyModalClose()
  }

  const onSubmit = (data: any) => {
    const { dose_quantity, start_at, end_at, ...attributes } = data
    delete attributes.api_doses

    const customFrequency = isCustomFrequencySelected ? removeEmptyValues(freqData!) : {}
    const frequencyId = isCustomFrequencySelected
      ? null
      : medFrequencies?.find(freq => freq.attributes.name === selectedFrequency)?.id

    const sharedAttributes = {
      ...attributes,
      comment: attributes.comment ?? '',
      custom_frequency: customFrequency,
      dose_unit: doseUnit,
      dose_quantity,
      ordered: true,
      start_at: moment(start_at).format('YYYY-MM-DD h:mm a'),
      end_at: moment(end_at).format('YYYY-MM-DD h:mm a')
    }

    const snapshot = buildSnapshot(data)
    const label = buildSessionLabel(data)

    if (editingClientId) {
      const existing = sessionAdded.find(item => item.clientId === editingClientId)
      if (existing?.id) {
        const payload = {
          data: {
            type: 'medication',
            attributes: {
              ...sharedAttributes,
              dosage: `${dose_quantity} ${doseUnit}`,
              medication_type:
                data.medication_type?.toUpperCase() === 'STAT' ? 'STAT' : data.medication_type || 'Routine'
            },
            relationships: {
              ...(frequencyId
                ? {
                    medication_frequency: {
                      data: { id: frequencyId, type: 'medication_frequency' }
                    }
                  }
                : {})
            }
          }
        }

        markSessionStatus(editingClientId, { status: 'pending', label, snapshot, payload })
        const medicationId = existing.id
        resetFormForNextOrder()
        runUpdateInBackground(editingClientId, medicationId, payload, label, snapshot)
        return
      }

      // Failed create retry — POST again for this session row
      const retryPayload = {
        data: {
          type: 'medication',
          attributes: {
            ...sharedAttributes,
            context: getMedicationContext(singleEncounter?.attributes.encounter_type, isDischargeTab),
            prescribed: true
          },
          relationships: {
            encounter: { data: { id: encounterId, type: 'encounter' } },
            patient: { data: { id: patientId, type: 'patient' } },
            staff: { data: { id: `${user?.id}`, type: 'staff' } },
            ...(frequencyId
              ? {
                  medication_frequency: {
                    data: { id: frequencyId, type: 'medication_frequency' }
                  }
                }
              : {})
          }
        }
      }
      markSessionStatus(editingClientId, { status: 'pending', label, snapshot, payload: retryPayload })
      resetFormForNextOrder()
      runCreateInBackground(editingClientId, retryPayload)
      return
    }

    const clientId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const payload = {
      data: {
        type: 'medication',
        attributes: {
          ...sharedAttributes,
          context: getMedicationContext(singleEncounter?.attributes.encounter_type, isDischargeTab),
          prescribed: true
        },
        relationships: {
          encounter: { data: { id: encounterId, type: 'encounter' } },
          patient: { data: { id: patientId, type: 'patient' } },
          staff: { data: { id: `${user?.id}`, type: 'staff' } },
          ...(frequencyId
            ? {
                medication_frequency: {
                  data: { id: frequencyId, type: 'medication_frequency' }
                }
              }
            : {})
        }
      }
    }

    setSessionAdded(prev => [...prev, { clientId, id: null, label, snapshot, status: 'pending', payload }])
    resetFormForNextOrder()
    runCreateInBackground(clientId, payload)
  }

  return (
    <>
      <FormProvider {...methods}>
        <Modal onClose={onClose} title={isEditing ? 'Edit Medication' : 'Add Medication'}>
          <form className="flex min-h-0 flex-1 flex-col overflow-hidden" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="grid gap-y-4 overflow-auto p-6">
              {sessionAdded.length > 0 ? (
                <div className="rounded-xl border border-[#E0E0E0] bg-[#F7F7F7] p-3">
                  <Text margin={{ bottom: '8px' }} size="14px" weight={500}>
                    Added this session
                  </Text>
                  <div className="space-y-2">
                    {sessionAdded.map(item => {
                      const statusLabel =
                        item.status === 'pending' ? 'Saving…' : item.status === 'error' ? 'Failed' : null
                      const canEdit = item.status !== 'pending'
                      return (
                        <div className="flex items-center justify-between gap-2" key={item.clientId}>
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <div className="flex items-start gap-2">
                              <Text size="14px">•</Text>
                              <Text size="14px">{item.label}</Text>
                            </div>
                            {statusLabel ? (
                              <Text
                                color={item.status === 'error' ? 'status-error' : undefined}
                                margin={{ left: '16px' }}
                                size="12px"
                              >
                                {statusLabel}
                              </Text>
                            ) : null}
                          </div>
                          <button
                            className="shrink-0 text-sm font-medium text-[#1565C0] hover:underline disabled:opacity-50"
                            disabled={!canEdit || editingClientId === item.clientId}
                            onClick={() => handleEditSessionMed(item)}
                            type="button"
                          >
                            {editingClientId === item.clientId ? 'Editing' : 'Edit'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              <Controller
                control={methods.control}
                name="nf_drug_name"
                render={({ field: { onChange } }) => (
                  <Select
                    borderRadius="25px"
                    createOptionPosition="first"
                    defaultOptions
                    getOptionValue={({ value }: any) => value}
                    isAsync
                    isCreatable
                    key={`drug-${editingClientId || 'new'}-${sessionAdded.length}`}
                    label="Drug name"
                    loadOptions={drugAction}
                    onChange={(val: any) => onChange(val?.value)}
                    value={drugVal}
                  />
                )}
              />

              <SelectInputField
                className="capitalize"
                label="Form"
                name="form"
                options={dosage_forms || []}
                placeholder="Choose dosage form"
              />

              <Controller
                control={methods.control}
                name="strength"
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  const selectedOption = value
                    ? availableStrengthOptions.find(option => option?.value === value) || { label: value, value }
                    : null

                  const handleCreateDrug = async (strength: string) => {
                    if (!drugName || !formValue) {
                      showToast('Please select drug name and drug form before creating a custom strength')
                      return
                    }
                    if (!STRENGTH_REGEX.test(strength)) {
                      showToast('Invalid format. Use "100mg" or "100mg + 200mg"')
                      return
                    }

                    try {
                      await createDrug({
                        data: {
                          type: 'drug',
                          attributes: { name: drugName, form: formValue, strength }
                        }
                      })
                      setCustomOptions(prev => [...prev, { label: strength, value: strength }])
                      onChange(strength)
                    } catch (err) {
                      getError(err)
                    }
                  }

                  const handleChange = (newValue: any) => {
                    if (!newValue) {
                      onChange(null)
                      return
                    }
                    onChange(typeof newValue === 'string' ? newValue : newValue?.value)
                  }

                  return (
                    <Select
                      borderRadius="25px"
                      createOptionPosition="first"
                      defaultOptions
                      error={error?.message}
                      getOptionValue={(option: any) => option.value}
                      isCreatable
                      label="Available Strength(s) / Calculated Dosage"
                      onChange={handleChange}
                      onCreateOption={handleCreateDrug}
                      options={availableStrengthOptions}
                      placeholder="eg 100mg + 200mg or 500mg"
                      value={selectedOption}
                    />
                  )
                }}
              />

              <div>
                <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
                  Dosage (Form Based)
                </Text>
                <div className="flex w-full items-center rounded-full border border-[#999999]">
                  <Controller
                    control={methods.control}
                    name="dose_quantity"
                    render={({ field: { value, onChange, onBlur, name, ref } }) => (
                      <Input
                        name={name}
                        onBlur={onBlur}
                        onChange={e => onChange(e.target.value)}
                        placeholder="Enter dosage"
                        ref={ref}
                        style={{ border: '0', height: '100%', padding: '', width: '300px' }}
                        type="number"
                        value={value == null ? '' : String(value)}
                      />
                    )}
                  />
                  <div className="borderless !items-center !border-none flex">
                    <div className="borderless h-8"></div>
                    <SearchInput
                      onChange={val => setDoseUnit(val?.value)}
                      options={dosage_unit.filter(unit => unit !== 'ml') || []}
                      placeholder="unit"
                      setValue={setDoseUnit}
                      style={{ border: 'none', outline: 0, padding: '', textTransform: 'lowercase' }}
                      value={doseUnit}
                    />
                  </div>
                </div>
              </div>

              <SelectInputField
                className="capitalize"
                label="Route"
                name="route"
                options={routes || []}
                placeholder="Choose route"
              />

              <div className="grid gap-y-2">
                <DropDownMenu
                  label="Select a frequency"
                  onCustomSelect={() => dispatch(showMedicationFrequency())}
                  onOptionSelect={setSelectedFrequency}
                  selectedValue={selectedFrequency}
                />
                <Controller
                  control={methods.control}
                  defaultValue={false}
                  name="once"
                  render={({ field }) => (
                    <CheckBox checked={field.value} label="Once" onChange={e => field.onChange(e.target.checked)} />
                  )}
                />
              </div>

              <SelectInputField
                label="Urgency"
                name="medication_type"
                options={['STAT', 'Routine']}
                placeholder="Select preferred regimen"
              />

              <DateInputField
                dateFormat="MMMM d, yyyy HH:mm"
                label="Start Date and Time"
                name="start_at"
                showTimeSelect
              />
              <DateInputField dateFormat="MMMM d, yyyy HH:mm" label="End Date and Time" name="end_at" showTimeSelect />

              {isDischargeTab && (
                <Controller
                  control={methods.control}
                  defaultValue={false}
                  name="administer_at_hospital"
                  render={({ field }) => (
                    <CheckBox
                      checked={field.value}
                      label="To be administered at the hospital"
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              )}

              <TextAreaField label="Note (optional)" name="comment" placeholder="Enter note" />
            </div>
            <FormButton
              addon={
                !isEditing && sessionAdded.length > 0 ? (
                  <Button
                    kinds="secondary"
                    label="Done"
                    onClick={onClose}
                    size="sm"
                    style={{ fontSize: '14px' }}
                    type="button"
                  />
                ) : null
              }
              cancel={isEditing ? 'Cancel edit' : 'Cancel'}
              isLoading={false}
              onClose={isEditing ? cancelEdit : onClose}
              submit={isEditing ? 'Update' : 'Add'}
            />
          </form>
        </Modal>
      </FormProvider>

      {custom_frequency.add && (
        <FormProvider {...freqMethods}>
          <Modal onClose={onFrequencyModalClose} title="Set Frequency">
            <form
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              onSubmit={freqMethods.handleSubmit(onFreqSubmit)}
            >
              <div className="grid gap-y-[1.7em] overflow-auto p-6">
                <div>
                  <Controller
                    control={freqMethods.control}
                    name="type"
                    render={({ field }) => (
                      <div className="mt-3 flex items-center gap-3 pb-3">
                        <RadioBtn
                          {...field}
                          checked={field.value === 'hourly'}
                          onChange={() => {
                            field.onChange('hourly')
                            handleHourlyCheck()
                          }}
                          value="hourly"
                        />
                        <Text color={isHourly ? '#1A1A1A' : '#3D3D3D'} size="small">
                          Hourly
                        </Text>
                      </div>
                    )}
                  />
                  <FormInputField
                    disabled={!isHourly}
                    label="Intervals"
                    labelClassName={`${!isHourly ? '!text-[#1A1A1A] !opacity-50' : '!text-[#3D3D3D]'} `}
                    name="interval"
                    placeholder="E.g every 5 hours"
                    style={{ color: isHourly ? '#1A1A1A' : '#3D3D3D', opacity: isHourly ? 1 : 0.5 }}
                  />
                </div>

                <div>
                  <Controller
                    control={freqMethods.control}
                    name="type"
                    render={({ field }) => (
                      <div className="mt-3 flex items-center gap-3 pb-3">
                        <RadioBtn
                          {...field}
                          checked={field.value === 'daily'}
                          onChange={() => {
                            field.onChange('daily')
                            handleDailyCheck()
                          }}
                          value="daily"
                        />
                        <Text color={!isHourly ? '#1A1A1A' : '#3D3D3D'} size="small">
                          Daily
                        </Text>
                      </div>
                    )}
                  />

                  <div
                    className="min-h-fit space-y-[0.9em] rounded-lg border border-[#999999] p-[0.9em]"
                    style={{ opacity: isHourly ? 0.5 : 1 }}
                  >
                    <div style={{ opacity: toggleStates.Specific_time ? 0.5 : 1 }}>
                      <div className="flex items-center justify-between">
                        <Text color={!isHourly ? '#1A1A1A' : '#3D3D3D'} size="16px">
                          Period of the day
                        </Text>
                        <ToggleBtn
                          disabled={isHourly}
                          id="Period"
                          isToggledOn={toggleStates.Period}
                          onToggle={handleToggle}
                        />
                      </div>
                      <Controller
                        control={freqMethods.control}
                        name="periods"
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <div className="mt-3">
                              {PERIODS_OF_DAY.map(list => (
                                <div className="mb-[15px] flex items-center gap-3" key={list}>
                                  <CheckBox
                                    checked={field.value?.includes(list)}
                                    disabled={!toggleStates.Period || isHourly}
                                    onChange={e => {
                                      const next = e.target.checked
                                        ? [...(field.value || []), list]
                                        : field.value?.filter((val: string | undefined) => val !== list)
                                      field.onChange(next)
                                    }}
                                    value={list}
                                  />
                                  <Text
                                    color={!toggleStates.Period || isHourly ? '#3D3D3D' : '#1A1A1A'}
                                    size="small"
                                    style={{ textTransform: 'capitalize' }}
                                  >
                                    {list}
                                  </Text>
                                </div>
                              ))}
                            </div>
                            {error?.message && (
                              <Text color="status-error" size="small">
                                {error.message}
                              </Text>
                            )}
                          </>
                        )}
                      />
                    </div>

                    <div style={{ opacity: toggleStates.Period ? 0.5 : 1 }}>
                      <div className="flex items-center justify-between pb-4 text-[16px]">
                        <Text color={!toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D'} size="16px">
                          Specific time of the day
                        </Text>
                        <ToggleBtn
                          disabled={isHourly}
                          id="Specific_time"
                          isToggledOn={toggleStates.Specific_time}
                          onToggle={handleToggle}
                        />
                      </div>
                      <div className="grid gap-y-3">
                        {timeFields.map((_, index) => (
                          <TimeInputField
                            className="uppercase"
                            disabled={!toggleStates.Specific_time || isHourly}
                            key={index}
                            label=""
                            name={`specific_times.${index}`}
                            style={{
                              color: toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D',
                              opacity: toggleStates.Specific_time ? 1 : 0.5
                            }}
                          />
                        ))}
                      </div>
                      <button
                        className="mt-2 flex items-center gap-x-[5px]"
                        onClick={() => setTimeFields(prev => [...prev, `specific_times_${prev.length}`])}
                        type="button"
                      >
                        <FaPlus color={palette.brand} />
                        <Text color={palette.brand} size="12px">
                          Add time
                        </Text>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <FormButton onClose={onFrequencyModalClose} submit="Set" />
            </form>
          </Modal>
        </FormProvider>
      )}
    </>
  )
}

export default AddMedication
